import { PoleCentrumKosztowego } from '@/components/PoleCentrumKosztowego';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { odstep } from '@/lib/odswiezanie';
import type { ColumnDef } from '@tanstack/react-table';
import { Clock, KeySquare, Loader2, Pencil, Plus, Split, Trash2, Upload, UserX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { OperacjeModal } from '@/components/OperacjeModal';
import { ZmianaNumeruKarty } from '@/components/ZmianaNumeruKarty';
import { Badge } from '@/components/ui/badge';
import { ZnacznikHr } from '@/components/ZnacznikHr';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { podzielBledy, tekstPaska } from '@/lib/bledy-serwera';
import {
  ZNACZNIKI_PRACOWNIKA,
  createEmployee,
  fetchCostCenterSuggestions,
  fetchEmployee,
  fetchEmployeeGroups,
  checkCardNumber,
  listEmployees,
  updateEmployee,
  type CentrumPodpowiedz,
  type EmployeeDetail,
  type EmployeeRow,
  type EmployeeWritePayload,
  type PoleZnacznika,
} from '@/lib/employees-api';
import { dataSps } from '@/lib/czas-sps';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { listMachines, type MachineRow } from '@/lib/machines-api';
import { ImportPracownikow } from '@/components/ImportPracownikow';
import { UsuwanieKart } from '@/components/UsuwanieKart';

const nf = new Intl.NumberFormat('pl-PL');

/**
 * Inicjały do awatara — PIERWSZE LITERY DWÓCH PIERWSZYCH CZŁONÓW nazwiska.
 *
 * ⚠️ Bez `toUpperCase()` byłoby „aL" u kogoś zapisanego małymi literami. Dane z SPS są wpisywane
 * ręcznie przy automacie i na DBW realnie mieszają wielkość liter w obrębie jednej listy
 * („Aanatolii Loksin" obok „ABATAYO NELSON").
 */
function inicjaly(nazwa: string): string {
  const czlony = nazwa.trim().split(/\s+/).filter(Boolean);
  if (czlony.length === 0) return '?';
  return (czlony[0]![0]! + (czlony[1]?.[0] ?? '')).toUpperCase();
}

/** Jeden automat lub jedna grupa na liście wyboru — wspólny kształt dla obu list. */
type Wybor = { id: string; etykieta: string; opis?: string; przezGrupe?: boolean };

// ─────────────────────────────── formularz ───────────────────────────────────

/**
 * NUMER KARTY jest wpisywany, nie generowany.
 *
 * `Users.USERNUMBER` to numer karty i zarazem klucz główny w SPS — jest wybity na karcie,
 * więc portal nie ma go skąd wymyślić. Wcześniej formularz pokazywał „pracownik dostanie
 * numer N" z `MAX(USERID)+1`, co brało się z nieprawdziwego założenia, że klucz jest liczbą
 * w sekwencji. Pole `descr` zniknęło: w SPS nazwa pracownika to JEDNA kolumna (`DESCR`),
 * więc drugie pole było fikcją i przy zapisie jedno nadpisywało drugie.
 */
/**
 * ⚠️ KSZTAŁT OBIEKTU WYJĘTY PRZED `superRefine` — potrzebujemy `.shape` do przypinania błędów
 * z serwera pod właściwe pole (`podzielBledy`). Po `superRefine` schemat nie jest już obiektem
 * i listy pól nie da się z niego odczytać, a wypisanie ich drugi raz z palca rozjechałoby się
 * przy pierwszym dodanym polu — i to CICHO: błąd serwera po prostu przestałby się przypinać.
 */
const FormObject = z
  .object({
    spsUserNumber: z
      .string()
      .trim()
      .min(1, 'Podaj numer karty')
      .max(32, 'Najwyżej 32 znaki — SPS obciąłby dłuższy bez ostrzeżenia'),
    name: z.string().trim().min(1, 'Podaj imię i nazwisko').max(50),
    /** `Users.ALIAS` — numer DODATKOWY, nie karta. */
    alias: z.string().trim().max(50).optional(),
    costCenter: z.string().trim().max(50).optional(),
    /** `Users.ALLOWLOGIN` — jedyny sposób archiwizacji pracownika w SPS. */
    active: z.boolean(),
    /** `<select>` oddaje tekst, więc trzymamy tekst i zamieniamy dopiero przy wysyłce. */
    rationEffect: z.enum(['1', '2', '3']),
    /**
     * ⚠️ PIN: PRZEŁĄCZNIK JEST OSOBNYM POLEM I MUSI BYĆ. Pole na PIN jest puste ZAWSZE, bo
     * PIN-u nie da się odczytać. Gdyby o wysyłce decydowała pustka pola, poprawienie nazwiska
     * kasowałoby ludziom kod do automatu — bez objawu aż do chwili, gdy ktoś stanie przed
     * maszyną.
     */
    zmienPin: z.boolean(),
    pin: z.string().trim().max(50),
    dateRangeEnable: z.boolean(),
    /** Data ŚCIENNA `RRRR-MM-DD`. Bez strefy — to data kalendarzowa maszyny klienta. */
    dateRangeStart: z.string(),
    dateRangeEnd: z.string(),
    ...(Object.fromEntries(ZNACZNIKI_PRACOWNIKA.map(({ pole }) => [pole, z.boolean()])) as Record<
      PoleZnacznika,
      z.ZodBoolean
    >),
  });

/** Nazwy pól formularza — po nich podpinamy błędy walidacji z serwera. */
const POLA_FORMULARZA = Object.keys(FormObject.shape);

const FormSchema = FormObject
  .superRefine((v, ctx) => {
    // Odwrócony zakres nie jest „prawie pusty" — nie ma w nim ANI JEDNEGO dnia, więc konto
    // przestaje działać natychmiast. SPS przyjmie taki wiersz bez zastrzeżeń.
    if (v.dateRangeEnable && v.dateRangeStart && v.dateRangeEnd && v.dateRangeStart > v.dateRangeEnd) {
      ctx.addIssue({ code: 'custom', path: ['dateRangeEnd'], message: 'emp.dateRangeOrder' });
    }
  });

type FormValues = z.infer<typeof FormSchema>;

/** Pole wyboru z etykietą — jedyny kształt „ptaszka" w tym formularzu. */
function Ptaszek({
  checked,
  onChange,
  label,
  kolumna,
  hint,
  disabled,
  gesto,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  /** Nazwa kolumny SPS — to po niej operator rozpozna ustawienie w SPSUtilities. */
  kolumna?: string;
  hint?: string;
  disabled?: boolean;
  /**
   * Gęściej — dla długich list wyboru.
   *
   * ⚠️ OSOBNY PRZEŁĄCZNIK, NIE ZMIANA DLA WSZYSTKICH. Ten sam „ptaszek" obsługuje też sekcję
   * ustawień pracownika, gdzie wierszy jest kilkanaście i ścieśnianie ich nic nie daje, a pogarsza
   * czytelność. Zamawiane było zmniejszenie odstępów W LIŚCIE GRUP.
   */
  gesto?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-2 rounded-md text-sm ${gesto ? 'px-1.5 py-0.5' : 'p-1.5'} ${
        disabled ? 'opacity-50' : 'cursor-pointer hover:bg-accent/50'
      }`}
      title={hint}
    >
      <input
        type="checkbox"
        className="mt-0.5 size-3.5 shrink-0 accent-[var(--primary)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0">
        <span className="block leading-snug">{label}</span>
        {kolumna && <span className="block font-mono text-[10px] leading-tight text-muted-foreground">{kolumna}</span>}
      </span>
    </label>
  );
}

/**
 * Lista wyboru wielokrotnego z licznikiem i przyciskiem „zaznacz/odznacz wszystkie".
 *
 * Wspólna dla grup i automatów, bo obie mają tę samą pułapkę: przycisk musi operować na
 * PEŁNEJ liście, a nie na tym, co akurat widać.
 */
function ListaWyboru({
  pozycje,
  wybrane,
  onChange,
  pusto,
  hint,
  szukajka,
  zaznaczWszystkie,
}: {
  pozycje: Wybor[];
  wybrane: string[];
  onChange: (ids: string[]) => void;
  pusto: string;
  hint?: string;
  /**
   * Szukajka i wybrane na górze — dla list, które bywają długie.
   *
   * ⚠️⚠️ ZAMAWIANE DLA GRUP PRACOWNICZYCH (04.09.2026): „niektóre firmy potrafią mieć po
   * 150+ grup". Włączane WPROST, nie automatycznie po długości: pole, które pojawia się samo przy
   * trzynastej pozycji, znaczyłoby, że ten sam ekran wygląda inaczej u dwóch klientów bez żadnego
   * powodu widocznego dla patrzącego.
   */
  szukajka?: boolean;
  /**
   * Czy pokazać „zaznacz/odznacz wszystkie".
   *
   * ⚠️⚠️ ZDJĘTE Z GRUP NA ŻĄDANIE (04.09.2026): „na ekranie pracownika nie musi być przycisku
   * »zaznacz wszystkie« na grupach". I to jest słuszne nie tylko z wygody: przy 150 grupach
   * przypisanie kogoś do WSZYSTKICH nie jest żadną realną czynnością, a jedno kliknięcie robiło
   * 150 członkostw — czyli 150 wierszy do bazy klienta i tyle samo do odkręcania.
   *
   * ⚠️ PRZY AUTOMATACH ZOSTAJE: tam „wszystkie" znaczy „ta osoba pobiera z całej floty" i jest
   * to zwykły przypadek u małych klientów.
   */
  zaznaczWszystkie?: boolean;
}) {
  const { t } = useI18n();
  const [fraza, setFraza] = useState('');
  const zbior = new Set(wybrane);
  /**
   * ⚠️⚠️ „WSZYSTKIE" LICZY SIĘ Z PEŁNEJ LISTY, NIE Z WIDOCZNEJ. Ten komponent miał to
   * ostrzeżenie w nagłówku, zanim była szukajka — a szukajka zamienia je w realną pułapkę:
   * „zaznacz wszystkie" po wpisaniu frazy zaznaczyłoby TYLKO odfiltrowane i cicho odznaczyło
   * resztę. Dlatego i przycisk, i licznik, i warunek „czy wszystkie" pytają o `pozycje`.
   */
  const wszystkie = pozycje.length > 0 && pozycje.every((p) => zbior.has(p.id));

  const widoczne = useMemo(() => {
    const szukane = fraza.trim().toLocaleLowerCase('pl');
    const dopasowane =
      szukane === ''
        ? pozycje
        : pozycje.filter(
            (p) =>
              p.etykieta.toLocaleLowerCase('pl').includes(szukane) ||
              (p.opis ?? '').toLocaleLowerCase('pl').includes(szukane),
          );
    if (szukajka !== true) return dopasowane;
    /**
     * ⚠️⚠️ ZAZNACZONE NA GÓRZE, ALFABETYCZNIE — zamawiane wprost. Przy 150 grupach zaznaczone
     * dwie potrafią być na pozycji 8 i 141, więc bez tego nie da się jednym spojrzeniem sprawdzić,
     * DO CZEGO ta osoba należy.
     *
     * ⚠️ ODZNACZONE WRACAJĄ NA SWOJE MIEJSCE, bo resztę zostawiamy w KOLEJNOŚCI WEJŚCIOWEJ —
     * nie sortujemy jej od nowa. Ta lista przychodzi już ułożona i drugie sortowanie mogłoby się
     * z tamtym rozjechać.
     */
    const wybraneNaGorze = dopasowane
      .filter((p) => zbior.has(p.id))
      .slice()
      .sort((a, b) => a.etykieta.localeCompare(b.etykieta, 'pl'));
    return [...wybraneNaGorze, ...dopasowane.filter((p) => !zbior.has(p.id))];
  }, [pozycje, fraza, szukajka, wybrane]);

  if (pozycje.length === 0) return <p className="text-xs text-muted-foreground">{pusto}</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {t('emp.selected').replace('{n}', String(wybrane.length)).replace('{total}', String(pozycje.length))}
        </span>
        {zaznaczWszystkie !== false && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onChange(wszystkie ? [] : pozycje.map((p) => p.id))}
          >
            {wszystkie ? t('emp.machinesClear') : t('emp.machinesAll')}
          </Button>
        )}
      </div>
      {szukajka === true && (
        <Input
          value={fraza}
          onChange={(e) => setFraza(e.target.value)}
          placeholder={t('emp.grupySzukaj')}
          className="h-8 text-xs"
          autoComplete="off"
        />
      )}
      {/* ⚠️ Wyżej o ~32 px i gęściej — zamawiane; przy 150 grupach każdy wiersz mniej to jedno przewinięcie mniej. */}
      <div className="max-h-60 overflow-y-auto rounded-md border border-border p-1">
        {szukajka === true && widoczne.length === 0 && (
          <p className="p-2 text-xs text-muted-foreground">{t('emp.grupyBrakTrafien')}</p>
        )}
        {widoczne.map((p) => (
          <div key={p.id} className="flex items-center gap-2">
            <span className="min-w-0 flex-1">
              <Ptaszek
                checked={zbior.has(p.id)}
                onChange={(v) => onChange(v ? [...wybrane, p.id] : wybrane.filter((x) => x !== p.id))}
                label={p.etykieta}
                kolumna={p.opis}
                gesto={szukajka === true}
              />
            </span>
            {/* ⚠️ Dostęp z grupy pokazany WPROST: odznaczenie tutaj go nie odbierze,
                a bez tej informacji wygląda to na usterkę portalu. */}
            {p.przezGrupe && (
              <Badge variant="neutral" title={t('emp.machinesViaGroupHint')}>
                {t('emp.machinesViaGroup')}
              </Badge>
            )}
          </div>
        ))}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * Czy dwie listy powiązań opisują TEN SAM zbiór (kolejność nieistotna).
 *
 * ⚠️ OD TEGO ZALEŻY, CZY ZAPIS RUSZY POWIĄZANIA. Formularz wysyła `employeeGroupIds`
 * i `machineIds` TYLKO wtedy, gdy realnie się różnią od wczytanych — bo pusta lista znaczy
 * po stronie API „odbierz wszystkie". Gdyby ekran z jakiegokolwiek powodu pokazał zero
 * zaznaczonych grup (nie wczytał, wczytał nie te, pomylił wyspę), zapis nazwiska usunąłby
 * pracownikowi członkostwa. Zgłoszone jako „nie wiem, czy zapis nie wyjebałby pracownika
 * z grupy" — i to jest dokładnie ta obawa, tyle że rozwiązana po stronie wysyłki, a nie
 * zaufania do tego, co widać.
 */
function tenSamZbior(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const x = new Set(a);
  return b.every((id) => x.has(id));
}

/** Nagłówek sekcji formularza — sam tekst, bez ramki, żeby okno nie zrobiło się kratą. */
function Sekcja({ tytul, children }: { tytul: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tytul}</h3>
      {children}
    </section>
  );
}

/**
 * Formularz pracownika — CAŁY wiersz `Users`, żeby nie trzeba było iść do SPSUtilities.
 *
 * ⚠️ MONTUJE SIĘ DOPIERO Z KOMPLETEM DANYCH i to jest warunek poprawności, nie wygody.
 * `useForm` zapamiętuje wartości domyślne przy PIERWSZYM renderze. Gdyby okno wstało przed
 * odpowiedzią serwera, wszystkie znaczniki miałyby wartość „wyłączone", a zapis wypchnąłby te
 * zera do SPS — czyli ekran skasowałby ustawienia zrobione przy automacie, których sam nie
 * zdążył pokazać. To ten sam scenariusz, przed którym broni migracja 0033 po stronie danych.
 */
function EmployeeForm({
  detail,
  groupId,
  machines,
  machinesTotal,
  groups,
  centraPodpowiedzi,
  onZmienNumer,
  onOpenChange,
  onSaved,
}: {
  /** `null` = nowy pracownik. */
  detail: EmployeeDetail | null;
  groupId: string;
  machines: MachineRow[];
  machinesTotal: number;
  groups: { id: string; name: string; spsGroupNumber: string }[];
  /** Kody centrum kosztowego realnie używane u ludzi (plus słownik) — do podpowiedzi. */
  centraPodpowiedzi: CentrumPodpowiedz[];
  /** Otwarcie osobnego okna zmiany numeru karty (tylko przy edycji). */
  onZmienNumer?: () => void;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      spsUserNumber: detail?.spsUserNumber ?? '',
      name: detail?.name ?? '',
      alias: detail?.alias ?? '',
      costCenter: detail?.costCenter ?? '',
      active: detail?.active ?? true,
      rationEffect: (String(detail?.rationEffect ?? 3) as '1' | '2' | '3') ?? '3',
      zmienPin: false,
      pin: '',
      dateRangeEnable: detail?.dateRangeEnable ?? false,
      dateRangeStart: detail?.dateRangeStart ?? '',
      dateRangeEnd: detail?.dateRangeEnd ?? '',
      /**
       * ⚠️ PRZY EDYCJI ZAWSZE WARTOŚĆ Z SPS, nawet gdy znacznik ma wartość domyślną.
       * Domyślna dotyczy wyłącznie NOWEGO pracownika; podstawienie jej przy edycji
       * nadpisałoby ustawienie zrobione przy automacie.
       */
      ...(Object.fromEntries(
        ZNACZNIKI_PRACOWNIKA.map((z) => [z.pole, detail ? detail[z.pole] : ('domyslnie' in z ? z.domyslnie : false)]),
      ) as Record<PoleZnacznika, boolean>),
    },
  });

  /**
   * ⚠️ DOMYŚLNIE WSZYSTKIE AUTOMATY przy zakładaniu pracownika — tak poprosił użytkownik i tak
   * jest bezpieczniej: pracownik bez ani jednego przypisania nie pobierze niczego nigdzie,
   * a to wygląda jak awaria automatu, nie jak brak uprawnienia.
   */
  const [employeeGroupIds, setEmployeeGroupIds] = useState<string[]>(detail?.employeeGroupIds ?? []);
  const [machineIds, setMachineIds] = useState<string[]>(detail ? detail.machineIds : machines.map((m) => m.id));

  const zGrupy = new Set(detail?.machineIdsFromGroups ?? []);
  const cardTyped = watch('spsUserNumber')?.trim() ?? '';
  const zmienPin = watch('zmienPin');
  const pin = watch('pin');
  const dateRangeEnable = watch('dateRangeEnable');

  /**
   * Kod z `Users.COSTCENTER`, którego encja `UserCostCenters` NIE ZNA.
   *
   * Taki kod istnieje w danych klienta i lista wyboru musi go pokazać jako osobną pozycję —
   * inaczej samo otwarcie edycji podmieniałoby wartość na pustą i zapis wyczyściłby pole,
   * którego nikt nie chciał ruszać.
   */
  /**
   * Sprawdzenie kolizji numeru karty — ZASTĄPIŁO podpowiadanie numeru.
   *
   * Patrzy też na kolejkę config-down: karta może być wolna w read-modelu i jednocześnie
   * zajęta operacją, która czeka na wykonanie. Porównanie jest niewrażliwe na wielkość liter,
   * bo taka jest kolacja SPS.
   */
  const cardCheck = useQuery({
    queryKey: ['employees', 'check-card', groupId, cardTyped],
    queryFn: () => checkCardNumber(groupId, cardTyped),
    enabled: detail === null && cardTyped.length > 0,
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    const znaczniki = Object.fromEntries(
      ZNACZNIKI_PRACOWNIKA.map(({ pole }) => [pole, values[pole]]),
    ) as Record<PoleZnacznika, boolean>;

    const wspolne: EmployeeWritePayload = {
      name: values.name,
      alias: values.alias?.trim() ? values.alias.trim() : null,
      costCenter: values.costCenter?.trim() ? values.costCenter.trim() : null,
      active: values.active,
      rationEffect: Number(values.rationEffect) as 1 | 2 | 3,
      ...znaczniki,
      dateRangeEnable: values.dateRangeEnable,
      /**
       * ⚠️ PUSTE POLE DATY JEDZIE JAKO `null`, a nie jako pominięcie. Pominięcie znaczyłoby
       * „zostaw jak było" i wyczyszczenie granicy nie dałoby się w ogóle wykonać.
       */
      dateRangeStart: values.dateRangeEnable && values.dateRangeStart ? values.dateRangeStart : null,
      dateRangeEnd: values.dateRangeEnable && values.dateRangeEnd ? values.dateRangeEnd : null,
      /**
       * ⚠️ WYSYŁAMY TYLKO TO, CO SIĘ ZMIENIŁO. `undefined` znaczy dla API „nie ruszaj",
       * `[]` znaczy „odbierz wszystkie" — patrz `tenSamZbior` wyżej.
       */
      ...(detail === null || !tenSamZbior(employeeGroupIds, detail.employeeGroupIds)
        ? { employeeGroupIds }
        : {}),
      ...(detail === null || !tenSamZbior(machineIds, detail.machineIds) ? { machineIds } : {}),
      /** ⚠️ PIN wyłącznie na jawne życzenie — inaczej nie ma go w ładunku w ogóle. */
      ...(values.zmienPin ? { zmienPin: true, pin: values.pin } : {}),
    };

    try {
      if (detail) {
        await updateEmployee(detail.id, wspolne);
        toast.success(t('emp.saved'));
      } else {
        const res = await createEmployee({
          ...wspolne,
          groupId,
          spsUserNumber: values.spsUserNumber,
          name: values.name,
        });
        toast.success(`${t('emp.created')} (${res.spsUserNumber})`);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (!(err instanceof ApiError)) {
        setServerError(t('common.error'));
        return;
      }
      /**
       * ⚠️ ODMOWA WALIDACJI IDZIE POD KONKRETNE POLE, nie tylko do paska. Bez tego okno pokazywało
       * gołe „Validation failed" i przy tylu polach nie dawało się zgadnąć, które zawiniło —
       * dokładnie to zgłoszenie naprawiamy. To, czego nie da się przypiąć (np. `groupId`, którego
       * na ekranie nie ma), dopisujemy do paska, żeby nie zniknęło bez śladu.
       */
      const { podpiete, reszta } = podzielBledy(err.pola, POLA_FORMULARZA);
      for (const p of podpiete) setError(p.pole as keyof FormValues, { type: 'server', message: p.komunikat });
      setServerError(tekstPaska(err.message, reszta));
      if (podpiete.length > 0) {
        document.getElementById(podpiete[0]!.pole)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormGrid>
        <Sekcja tytul={t('emp.section.basic')}>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* ⚠️ POLE JEST ZAWSZE, ale przy edycji WYŁĄCZONE — układ okna ma być ten sam
                w obu trybach, żeby nie trzeba było szukać, co gdzie jest. Numer karty to klucz
                naturalny rozsiany po kilkunastu tabelach SPS; jego zmiana to kaskada, którą
                wykonuje własna procedura SPS, a nie patch pojedynczego wiersza. Pokazujemy
                go więc na szaro, zamiast chować. */}
            <Field
              label={t('emp.cardNumber')}
              htmlFor="spsUserNumber"
              required={!detail}
              error={errors.spsUserNumber?.message}
            >
              <Input
                id="spsUserNumber"
                autoComplete="off"
                autoFocus={!detail}
                disabled={Boolean(detail)}
                placeholder="np. 0009999"
                {...register('spsUserNumber')}
              />
              {/**
                * ⚠️ PRZYCISK ZAMIAST ODBLOKOWANIA POLA. Numer karty jest kluczem naturalnym
                * w SPS — jego zmiana to kaskada DELETE+INSERT po czterech tabelach, a przy
                * zaznaczonym ptaszku także po rejestrze wydań. Gdyby dało się go po prostu
                * nadpisać w tym formularzu, literówka przy poprawianiu nazwiska uruchamiałaby
                * tę kaskadę na bazie klienta.
                */}
              {detail && onZmienNumer && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-xs"
                  onClick={onZmienNumer}
                >
                  {t('emp.cardChangeAction')}
                </Button>
              )}
              {!detail && (
                <>
                {cardTyped.length > 0 && cardCheck.data && !cardCheck.data.free && (
                  <p className="mt-1 text-xs text-[var(--destructive)]">
                    {t('emp.cardTaken').replace('{name}', cardCheck.data.takenBy?.name ?? '?')}
                  </p>
                )}
                {cardTyped.length > 0 && cardCheck.data?.free && cardCheck.data.pending && (
                  <p className="mt-1 text-xs text-[var(--warning)]">{t('emp.cardPending')}</p>
                )}
                </>
              )}
            </Field>

            <Field label={t('emp.name')} htmlFor="name" required error={errors.name?.message}>
              <Input id="name" autoComplete="off" autoFocus={Boolean(detail)} {...register('name')} />
            </Field>

            <Field label={t('emp.alias')} htmlFor="alias" error={errors.alias?.message}>
              <Input id="alias" autoComplete="off" {...register('alias')} />
            </Field>

            {/* LISTA WYBORU, nie wolne pole.
                Wolny napis znaczył, że literówka tworzy centrum-widmo, po którym nikt nie
                zaksięguje — encji o takim kodzie po prostu nie ma. Wartości poza listą nadal
                istnieją w danych klienta, więc pokazujemy je jako osobną pozycję „poza słownikiem",
                zamiast po cichu zamieniać na puste. */}
            {/**
             * ⚠️ POLE TEKSTOWE Z PODPOWIEDZIAMI, NIE LISTA ZAMKNIĘTA — i to jest poprawka,
             * nie zmiana zdania. Poprzednia wersja budowała listę ze słownika `UserCostCenters`
             * i broniła się tym, że wolny napis tworzy „centrum-widmo". Tyle że słownik jest
             * u klientów PUSTY: na DBW zero encji przy 472 osobach z kodem wpisanym ręcznie.
             * Lista miała więc jedną pozycję („bez centrum") i pole było w praktyce martwe —
             * nie dało się ani wybrać istniejącego kodu, ani wpisać nowego.
             *
             * `<datalist>` daje jedno i drugie: podpowiada to, co realnie stoi u ludzi
             * (z licznikiem, więc widać, który wariant jest właściwy), a mimo to przyjmuje
             * dowolny tekst — bo tak działa SPS i tak pracuje każdy klient.
             */}
            {/* ⚠️ BEZ PODPISU POD POLEM. Wyjaśnienie „wpisz z ręki albo wybierz z podpowiedzi"
                opisywało zachowanie, które widać po samym otwarciu listy — a zajmowało trzy
                wiersze w oknie, w którym każdy element ma konkurować o uwagę z sensem. */}
            {/*
              ⚠️⚠️ WŁASNA LISTA, NIE `<datalist>` (zgłoszone 01.09.2026: „miało podpowiadać rzeczy
              które są w centrach kosztowych — teraz jest to zwykłe pole tekstowe i nie podpowiada").
              Podpowiedzi jechały z serwera jak trzeba; `<datalist>` nie rozwijał się w Chrome ani
              w Edge przy `autocomplete="off"`, więc pole wyglądało na goły tekst. Uzasadnienie
              i zachowanie stoją w `PoleCentrumKosztowego`.
            */}
            <Field label={t('emp.costCenter')} htmlFor="costCenter" error={errors.costCenter?.message}>
              <PoleCentrumKosztowego
                id="costCenter"
                wartosc={watch('costCenter') ?? ''}
                podpowiedzi={centraPodpowiedzi}
                placeholder={t('emp.costCenterNone')}
                onZmiana={(v) => setValue('costCenter', v, { shouldDirty: true })}
              />
            </Field>

            <Field label={t('emp.rationEffect')} htmlFor="rationEffect">
              <Select id="rationEffect" {...register('rationEffect')}>
                <option value="3">{t('emp.rationEffect3')}</option>
                <option value="2">{t('emp.rationEffect2')}</option>
                <option value="1">{t('emp.rationEffect1')}</option>
              </Select>
            </Field>
          </div>

          <Ptaszek
            checked={watch('active')}
            onChange={(v) => setValue('active', v, { shouldDirty: true })}
            label={t('emp.active')}
            kolumna="ALLOWLOGIN"
          />
        </Sekcja>

        <Sekcja tytul={t('emp.section.groups')}>
          <ListaWyboru
            pozycje={groups.map((g) => ({ id: g.id, etykieta: g.name, opis: g.spsGroupNumber }))}
            wybrane={employeeGroupIds}
            onChange={setEmployeeGroupIds}
            pusto={t('emp.groupsNone')}
            /**
             * ⚠️ SZUKAJKA WŁĄCZONA TYLKO TU — zamawiane dla grup pracowniczych („niektóre firmy
             * potrafią mieć po 150+ grup"). Lista automatów poniżej jej nie ma, bo o nią nie
             * proszono; ten sam znacznik ją tam włączy, gdy zajdzie potrzeba.
             */
            szukajka
            zaznaczWszystkie={false}
          />
        </Sekcja>

        <Sekcja tytul={t('emp.section.machines')}>
          <ListaWyboru
            pozycje={machines.map((m) => ({
              id: m.id,
              etykieta: `VMID ${m.vmid} — ${m.name}`,
              przezGrupe: zGrupy.has(m.id),
            }))}
            wybrane={machineIds}
            onChange={setMachineIds}
            pusto={t('emp.machinesEmpty')}
          />
          {/* ⚠️ Lista automatów jedzie jedną stroną (API daje najwyżej 200). Gdyby wyspa miała
              więcej, „zaznacz wszystkie" zaznaczyłoby część i nikt by się nie zorientował —
              więc mówimy to wprost, zamiast po cichu obcinać. */}
          {machinesTotal > machines.length && (
            <p className="text-xs text-[var(--warning)]">
              Widać {machines.length} z {machinesTotal} automatów — reszta nie mieści się na jednej
              stronie, więc „zaznacz wszystkie" nie obejmie ich wszystkich.
            </p>
          )}
        </Sekcja>

        <Sekcja tytul={t('emp.section.pin')}>
          <Ptaszek
            checked={zmienPin}
            onChange={(v) => setValue('zmienPin', v, { shouldDirty: true })}
            label={t('emp.pinChange')}
            kolumna="PASSWORD"
          />
          <Field label={t('emp.pinLabel')} htmlFor="pin" error={errors.pin?.message}>
            <Input
              id="pin"
              type="password"
              autoComplete="new-password"
              disabled={!zmienPin}
              placeholder={zmienPin ? '' : '—'}
              {...register('pin')}
            />
          </Field>
          {/* Wyczyszczenie PIN-u jest legalne, ale musi być świadome — nie „bo pole było puste". */}
          {zmienPin && pin.trim() === '' && (
            <p className="text-xs text-[var(--warning)]">{t('emp.pinClearWarn')}</p>
          )}
        </Sekcja>

        <Sekcja tytul={t('emp.section.more')}>
          <div className="grid gap-x-4 sm:grid-cols-2">
            {ZNACZNIKI_PRACOWNIKA.map(({ pole, kolumna }) => (
              <Ptaszek
                key={pole}
                checked={watch(pole)}
                onChange={(v) => setValue(pole, v, { shouldDirty: true })}
                label={t(`emp.flag.${pole}`)}
                kolumna={kolumna}
              />
            ))}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <Ptaszek
              checked={dateRangeEnable}
              onChange={(v) => setValue('dateRangeEnable', v, { shouldDirty: true })}
              label={t('emp.dateRangeEnable')}
              kolumna="DATERANGEENABLE"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('emp.dateRangeStart')} htmlFor="dateRangeStart" error={errors.dateRangeStart?.message}>
                <Input id="dateRangeStart" type="date" disabled={!dateRangeEnable} {...register('dateRangeStart')} />
              </Field>
              <Field
                label={t('emp.dateRangeEnd')}
                htmlFor="dateRangeEnd"
                error={errors.dateRangeEnd?.message && t(errors.dateRangeEnd.message)}
              >
                <Input id="dateRangeEnd" type="date" disabled={!dateRangeEnable} {...register('dateRangeEnd')} />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">{t('emp.dateRangeHint')}</p>
          </div>
        </Sekcja>

        <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {t('emp.columnsNotice')}
        </div>

        <ModalError>{serverError}</ModalError>
      </FormGrid>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="animate-spin" />}
          {t('common.save')}
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * Okno pracownika — WSPÓLNE dla listy i dla karty pracownika.
 *
 * ⚠️ EKSPORTOWANE ŚWIADOMIE, żeby karta nie miała własnego formularza. Ten formularz zna wszystkie
 * pułapki zapisu: `ALLOWLOGIN` wysyłane zawsze, PIN tylko na jawne życzenie, pusta lista powiązań
 * znacząca „odbierz wszystkie". Drugi, „prostszy" formularz na karcie musiałby znać je wszystkie
 * od nowa — a rozjazd byłby cichy aż do chwili, gdy ktoś stanie przed automatem.
 *
 * ⚠️ `editing` TO MINIMALNY KSZTAŁT, nie cały wiersz listy: karta zna tylko identyfikator i numer
 * karty, a reszta i tak dojeżdża osobnym odczytem (`fetchEmployee`).
 */
export function EmployeeModal({
  open,
  onOpenChange,
  editing,
  groupId,
  onSaved,
  onZmienNumer,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: { id: string; spsUserNumber: string } | null;
  groupId: string;
  onSaved: () => void;
  onZmienNumer?: () => void;
}) {
  const { t } = useI18n();
  const { islands } = useIslands();
  /** Dane z HR+ (nazwa, MPK, blokada) i tak zostaną nadpisane — trzeba to powiedzieć PRZED edycją. */
  const hrPlus = islands.find((i) => i.id === groupId)?.hrPlus === true;

  /** Pełne ustawienia edytowanego pracownika — lista ich nie niesie. */
  const detail = useQuery({
    queryKey: ['employees', 'detail', editing?.id],
    queryFn: () => fetchEmployee(editing!.id),
    enabled: Boolean(editing),
  });

  /**
   * ⚠️ PODPOWIEDZI Z DANYCH, NIE ZE SŁOWNIKA. Słownik `UserCostCenters` jest u klientów pusty
   * (DBW: zero encji przy 472 osobach z kodem), więc lista budowana z niego nie dawała nic.
   */
  const centra = useQuery({
    queryKey: ['employees', 'cost-centers', groupId],
    queryFn: () => fetchCostCenterSuggestions(groupId),
    enabled: Boolean(groupId),
  });

  const groups = useQuery({
    queryKey: ['employees', 'groups', groupId],
    queryFn: () => fetchEmployeeGroups(groupId),
    enabled: Boolean(groupId),
  });

  const machines = useQuery({
    queryKey: ['machines', 'picker', groupId],
    queryFn: () =>
      listMachines({ groupId, page: 1, pageSize: 200, sort: 'vmid', dir: 'asc', onlyOnline: false }),
    enabled: Boolean(groupId),
  });

  /**
   * ⚠️ CZEKAMY NA WSZYSTKO, ŁĄCZNIE Z LISTAMI. Nie tylko dlatego, że formularz bez nich jest
   * niepełny: przy NOWYM pracowniku domyślnie zaznaczamy wszystkie automaty, więc okno otwarte
   * przed ich wczytaniem zaznaczyłoby zero i założyłoby pracownika bez dostępu do czegokolwiek.
   */
  const gotowe =
    (editing === null || detail.data !== undefined) &&
    centra.data !== undefined &&
    groups.data !== undefined &&
    machines.data !== undefined;

  const blad = detail.error ?? groups.error ?? machines.error ?? centra.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(46rem,calc(100vw-2rem))]"
        title={editing ? t('emp.editTitle') : t('emp.addTitle')}
        description={editing ? `${t('emp.cardNumber')}: ${editing.spsUserNumber}` : undefined}
      >
        {hrPlus && editing && (
          <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">{t('emp.hrPlusEdycja')}</p>
        )}
        {blad ? (
          <ModalError>{t('emp.detailError')}</ModalError>
        ) : !gotowe ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('emp.detailLoading')}
          </p>
        ) : (
          <EmployeeForm
            detail={detail.data ?? null}
            groupId={groupId}
            machines={machines.data!.rows}
            machinesTotal={machines.data!.total}
            groups={groups.data!}
            centraPodpowiedzi={centra.data!.podpowiedzi}
            onZmienNumer={onZmienNumer}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── strona ──────────────────────────────────────

export function Employees() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { groupId, island } = useIslands();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 50,
    sort: 'name',
    dir: 'asc',
  });
  const [employeeGroupId, setEmployeeGroupId] = useState('');
  const [machineId, setMachineId] = useState('');
  /**
   * ⚠️ DOMYŚLNIE WSZYSCY, NIE TYLKO AKTYWNI — zmienione po feedbacku z użycia (2026-08-25).
   * „Aktywni" jako domyślny widok ukrywały część kadry BEZ ŚLADU na ekranie: człowiek szukał
   * kogoś, kto w SPS jest, i nie znajdował go, bo filtr milczał o tym, że coś odsiewa.
   * Ukrywanie danych domyślnie jest gorsze niż dłuższa lista — a stan i tak widać w kolumnie.
   */
  const [stan, setStan] = useState<'aktywni' | 'nieaktywni' | 'wszyscy'>('wszyscy');
  const [onlyMultiMachine, setOnlyMultiMachine] = useState(false);
  /** `?centrum=` — wejście z listy centrów kosztowych (kod z kart, 13.10.2026) od razu na jego ludzi. */
  const [adres] = useSearchParams();
  const [costCenter, setCostCenter] = useState(() => adres.get('centrum')?.trim() ?? '');
  /**
   * USUNIĘCI Z AUTOMATÓW (29.09.2026) — widoczni tylko w Vendi, a na liście dopiero po zaznaczeniu
   * filtra (zamówione wprost). Zaznaczony pokazuje WYŁĄCZNIE ich, bez względu na stan konta.
   */
  const [usunieci, setUsunieci] = useState(false);
  /** Znacznik HR+ (12.10.2026) — filtr widoczny tylko na wyspie z ustawieniami HR+. */
  const [hrFiltr, setHrFiltr] = useState<'' | 'z' | 'spoza' | 'wyjatki'>('');
  /** Okno usuwania kart — puste z nagłówka, z kartą z wiersza; `trwale` dla osoby już usuniętej. */
  const [usuwanie, setUsuwanie] = useState<{ numery?: string[]; trwale?: boolean } | null>(null);
  /**
   * CZYSZCZENIE FILTRÓW — jedno miejsce dla paska nad tabelą.
   *
   * ⚠️ `stan` WRACA DO „wszyscy", bo taka jest wartość początkowa tego ekranu. Powrót do
   * „aktywni" byłby innym widokiem niż ten, który człowiek zobaczył, wchodząc tu pierwszy raz.
   */
  const wyczyscFiltry = () => {
    setEmployeeGroupId('');
    setMachineId('');
    setCostCenter('');
    setOnlyMultiMachine(false);
    setStan('wszyscy');
    setUsunieci(false);
    setHrFiltr('');
    onStateChange({ page: 1 });
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [importOtwarty, setImportOtwarty] = useState(false);
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [operacjeFor, setOperacjeFor] = useState<EmployeeRow | null>(null);
  const [numerFor, setNumerFor] = useState<EmployeeRow | null>(null);

  const params = {
    ...state,
    groupId: groupId ?? '',
    employeeGroupId: employeeGroupId || undefined,
    machineId: machineId || undefined,
    costCenter: costCenter || undefined,
    stan,
    usunieci: usunieci ? ('tylko' as const) : undefined,
    hrPlus: hrFiltr || undefined,
    onlyMultiMachine,
  };

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['employees', params],
    queryFn: () => listEmployees(params),
    enabled: Boolean(groupId),
    /**
     * ⚠️ TO TEN EKRAN BYŁ ZGŁOSZONY (2026-08-18): „puścimy zmianę na pracowniku, wyskakuje zegarek,
     * że zmiana w toku, ale jak sami nie odświeżymy strony, to to się nigdy nie zmieni". Znacznik
     * gaśnie, gdy zmianę potwierdzi MASZYNA KLIENTA — czyli nigdy w odpowiedzi na kliknięcie tutaj.
     * Dopóki choć jeden wiersz czeka, lista chodzi szybko; potem sama zwalnia.
     */
    refetchInterval: (q) => odstep((q.state.data?.rows ?? []).some((r) => r.pending === true)),
  });

  const groups = useQuery({
    queryKey: ['employees', 'groups', groupId],
    queryFn: () => fetchEmployeeGroups(groupId!),
    enabled: Boolean(groupId),
  });

  const machines = useQuery({
    queryKey: ['machines', 'picker', groupId],
    queryFn: () =>
      listMachines({ groupId: groupId!, page: 1, pageSize: 200, sort: 'vmid', dir: 'asc', onlyOnline: false }),
    enabled: Boolean(groupId),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  /**
   * ⚠️ KODY DO FILTRA Z DANYCH PRACOWNIKÓW, nie ze słownika. Komentarz przy samym filtrze
   * obiecywał to od początku, ale kod brał `rows` ze słownika — a ten jest u klientów pusty,
   * więc lista rozwijana filtra była tam pusta i wyglądała na zepsutą.
   *
   * ⚠️ ZAPYTANIE `costCentersQ` ZNIKŁO RAZEM Z PASKIEM ROZJAZDU (28.08.2026). Stało tu z notą
   * „zostaje WYŁĄCZNIE dla paska rozjazdu, który potrzebuje `summary`" — pasek poszedł na wprost
   * zgłoszone życzenie, więc zapytanie nie miało już po co chodzić przy każdym wejściu na ekran.
   */
  const centraFiltr = useQuery({
    queryKey: ['employees', 'cost-centers', groupId],
    queryFn: () => fetchCostCenterSuggestions(groupId!),
    enabled: Boolean(groupId),
  });

  /**
   * NAZWY CZYNNYCH FILTRÓW dla paska nad tabelą (zgłoszone 02.09.2026 — „przede wszystkim
   * pracownicy, transakcje").
   *
   * ⚠️ WARTOŚĆ POCZĄTKOWA NIE JEST FILTREM. `stan = 'wszyscy'` to stan wejściowy tego ekranu,
   * więc na pasku nie może świecić — inaczej pasek świeciłby zawsze i przestałby cokolwiek znaczyć.
   */
  const aktywneFiltry = [
    employeeGroupId
      ? t('emp.group') + ': ' + ((groups.data ?? []).find((g) => g.id === employeeGroupId)?.name ?? employeeGroupId)
      : null,
    machineId
      ? t('emp.machine') +
        ': ' +
        (() => {
          const m = (machines.data?.rows ?? []).find((x) => x.id === machineId);
          return m === undefined ? machineId : `VMID ${m.vmid} — ${m.name}`;
        })()
      : null,
    onlyMultiMachine ? t('emp.onlyMulti') : null,
    stan === 'wszyscy' ? null : t('emp.state') + ': ' + t(stan === 'aktywni' ? 'emp.stateActive' : 'emp.stateInactive'),
    costCenter ? t('emp.costCenter') + ': ' + costCenter : null,
    usunieci ? t('emp.filtrUsunieci') : null,
    hrFiltr
      ? t('emp.hr.filtr') + ': ' + t(hrFiltr === 'z' ? 'emp.hr.filtrZ' : hrFiltr === 'wyjatki' ? 'emp.hr.filtrWyjatki' : 'emp.hr.filtrSpoza')
      : null,
  ].filter((x): x is string => x !== null);

  // Filtry wskazujące rekordy TEJ wyspy (automat, grupa) muszą zniknąć przy przełączeniu wyspy.
  // Inaczej po zmianie wyspy zostaje id z poprzedniej: lista wraca cicho pusta, a pasek aktywnych
  // filtrów pokazuje wtedy „—", bo nazwy nie da się znaleźć w nowym zbiorze — czyli filtr działa,
  // ale nie widać, że działa.
  useEffect(() => {
    setEmployeeGroupId('');
    setMachineId('');
    setCostCenter('');
  }, [groupId]);
  const resetPage = useCallback(() => onStateChange({ page: 1 }), [onStateChange]);

  /**
   * KOLUMNY W UKŁADZIE ZE STAREGO VENDI — ustalone przez porównanie zrzutu z NASZĄ kopią bazy DBW.
   *
   * ⚠️ DRUGI WIERSZ TO `nr {numer karty} · {alias} · CK {centrum}` i to jest POTWIERDZONE, nie
   * odgadnięte: cztery osoby z DBW zgodziły się co do znaku (0000DFB675A2 / 853 / M3IKS1KW itd.).
   * „CK " jest literalnym prefiksem, a nie danymi. Alias bywa numerem (DBW) albo nazwiskiem
   * (ARJO) — to wolne pole i każdy klient używa go inaczej, więc nie wolno go formatować.
   */
  const columns = useMemo<ColumnDef<EmployeeRow>[]>(
    () => [
      {
        id: 'karta',
        header: t('emp.cardNumber'),
        meta: { exportOnly: true, exportValue: (r) => r.spsUserNumber },
      },
      {
        id: 'name',
        header: t('emp.employee'),
        meta: { exportValue: (r) => r.name },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex items-center gap-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                {inicjaly(r.name)}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5">
                  {/* ⚠️ NAZWISKO JEST WEJŚCIEM NA KARTĘ — tak samo jak w starym Vendi. Osobny
                      przycisk „szczegóły" w kolumnie akcji byłby drugim miejscem do klikania
                      tego samego, a nazwisko to pierwsza rzecz, w którą celuje kursor. */}
                  <Link
                    to={`/pracownicy/${r.id}`}
                    className="truncate font-medium hover:text-[var(--primary)] hover:underline"
                    title={t('profile.open')}
                  >
                    {/* ⚠️ Pusta nazwa (SPS jej nie wymaga) dawała link bez tekstu — nie było w co kliknąć (30.10.2026). */}
                    {r.name.trim() === '' ? <span className="italic text-muted-foreground">{t('emp.bezNazwy')}</span> : r.name}
                  </Link>
                  {r.pending && (
                    <Badge variant="warning" title={t('emp.pendingHint')}>
                      <Clock className="size-3" />
                    </Badge>
                  )}
                  {r.usunietaZAutomatow && (
                    <Badge
                      variant="neutral"
                      title={t(r.usunietaZAutomatowWezel ? 'emp.usunietaHintWezel' : 'emp.usunietaHint')
                        .replace('{kiedy}', new Date(r.usunietaZAutomatow).toLocaleString())
                        .replace('{wezel}', r.usunietaZAutomatowWezel ?? '')}
                    >
                      <UserX className="size-3" />
                      {t('emp.usunieta')}
                    </Badge>
                  )}
                  <ZnacznikHr stan={r.hrPlus} numer={r.hrPlusNumer} />
                </span>
                {/* drugi wiersz — numer karty, alias i centrum kosztowe w jednej linii */}
                <span className="block truncate text-xs text-muted-foreground">
                  nr <span className="font-mono">{r.spsUserNumber}</span>
                  {r.alias && <> · {r.alias}</>}
                  {r.costCenter && <> · <span className="text-[var(--primary)]">CK {r.costCenter}</span></>}
                </span>
              </span>
            </span>
          );
        },
      },
      /**
       * ═══════════════════════════════════════════════════════════════════
       *  NUMER KARTY, ALIAS I CENTRUM KOSZTOWE — OSOBNE KOLUMNY, ALE TYLKO W EKSPORCIE.
       *
       *  ⚠️⚠️ ZGŁOSZONE 09.09.2026: „dodaj w eksporcie pracowników, żeby był też numer karty
       *  i centrum kosztowe (w tej chwili tego brakuje)". Na ekranie są od dawna — w drugim
       *  wierszu komórki z nazwiskiem — ale eksport bierze z kolumny JEDNĄ wartość, więc
       *  oddawał samo nazwisko i cicho gubił te dwie.
       *
       *  ⚠️⚠️ KOLEJNOŚĆ `karta, nazwisko, alias, centrum` JEST KOLEJNOŚCIĄ NASZEGO IMPORTU
       *  (`POLA_IMPORTU` w `import-pracownikow.ts`) — i to jest cały powód, dla którego tak stoi.
       *  Arkusz z eksportu da się wtedy wkleić z powrotem bez przestawiania kolumn, a numer karty
       *  jest kluczem naturalnym w SPS (cztery tabele), więc musi być pierwszy; nazwisko bywa
       *  niepełne i powtarzalne. Ekran ma tę samą kolejność w linijce pod nazwiskiem.
       *
       *  ⚠️ NIE DODAJEMY ICH DO TABELI NA EKRANIE — zgłoszenie dotyczy eksportu, a trzy nowe
       *  słupki pokazywałyby to samo, co linijka pod nazwiskiem, zwężając pozostałe kolumny.
       * ═══════════════════════════════════════════════════════════════════
       */
      {
        id: 'alias',
        header: t('emp.alias'),
        /**
         * ⚠️⚠️ ALIAS MIĘDZY NAZWISKIEM A CENTRUM — dołożony 09.09.2026 („alias też dodaj").
         * Ta kolejność to nie estetyka: `karta, nazwisko, alias, centrum` jest DOKŁADNIE
         * kolejnością pozycji naszego importu pracowników, więc arkusz z eksportu da się wkleić
         * z powrotem bez przestawiania kolumn. Ekran ma ją tak samo (linijka pod nazwiskiem).
         *
         * ⚠️ ALIAS TO WOLNE POLE i każdy klient używa go inaczej (numer u jednego, nazwisko
         * u drugiego) — oddajemy go BEZ formatowania, dokładnie tak, jak stoi w bazie.
         */
        meta: { exportOnly: true, exportValue: (r) => r.alias ?? '' },
      },
      {
        id: 'centrum',
        header: t('emp.costCenter'),
        /** ⚠️ Puste centrum oddajemy jako pusty napis, nie jako `null` — arkusz ma pokazać pustą komórkę. */
        meta: { exportOnly: true, exportValue: (r) => r.costCenter ?? '' },
      },
      {
        id: 'groups',
        header: t('emp.groups'),
        enableSorting: false,
        meta: { exportValue: (r) => r.groups.map((g) => g.name).join(', ') },
        cell: ({ row }) =>
          row.original.groups.length === 0 ? (
            // „bez grupy" szarym tekstem, nie kreską: brak grupy znaczy też brak reguł grupowych,
            // więc to informacja, a nie pusta komórka
            <span className="text-xs text-muted-foreground">{t('emp.noGroup')}</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {row.original.groups.map((g) => (
                <Badge key={g.id} variant="neutral">
                  {g.name}
                </Badge>
              ))}
            </span>
          ),
      },
      {
        id: 'machines',
        header: t('emp.machinesShort'),
        enableSorting: false,
        /** Eksport — wszystkie automaty, na których osoba pobiera: wprost i przez grupę. */
        meta: { exportValue: (r) => r.machineCount + r.machineCountZGrup, align: 'center' },
        /**
         * ⚠️⚠️ PRZEZ GRUPĘ OSOBNO, NIE W JEDNEJ LICZBIE (14.10.2026, TYCO): samo „0" mówiło o człowieku, którego
         * automat wpuszcza przez grupę — po odznaczeniu automatu w HR+ wyglądało na odcięcie, a nie było nim.
         * Jedna suma też by myliła: odznaczenie przy osobie zdejmuje tylko przypisania wprost.
         */
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            {row.original.multiMachine ? (
              // to jest przypadek, w którym limit liczony per-automat wychodzi za mały
              <Badge variant="warning" title={t('emp.multiHint')}>
                <Split className="size-3" />
                {row.original.machineCount}
              </Badge>
            ) : (
              <span className="tabular-nums">{row.original.machineCount}</span>
            )}
            {row.original.machineCountZGrup > 0 && (
              <Badge variant="neutral" title={t('emp.machinesZGrupHint')}>
                {t('emp.machinesZGrup').replace('{n}', String(row.original.machineCountZGrup))}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'limits',
        header: t('emp.limitsShort'),
        enableSorting: false,
        meta: { exportValue: (r) => r.limits, align: 'right' },
        cell: ({ row }) => {
          const r = row.original;
          /**
           * ⚠️ ZERO LICZY SIĘ TAK SAMO JAK `RATIONEFFECT` 1/2: w obu wypadkach automat nie
           * pilnuje tej osoby, tylko z innego powodu — raz nie ma reguły, raz jest wyłączona.
           * Ekran musi rozróżniać powód, bo naprawia się je gdzie indziej.
           */
          if (r.rationEffect !== 3) {
            return (
              <Badge variant="warning" title={t('emp.rationOff')}>
                {r.limits}
              </Badge>
            );
          }
          return (
            <span className={`tabular-nums ${r.limits === 0 ? 'text-muted-foreground' : ''}`}>{r.limits}</span>
          );
        },
      },
      {
        id: 'activity',
        header: t('emp.activity'),
        enableSorting: false,
        meta: { exportValue: (r) => (r.txCount > 0 ? `${r.txCount} tx / ${r.lastTxAt ?? ''}` : '') },
        cell: ({ row }) => {
          const r = row.original;
          if (r.txCount === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="whitespace-nowrap text-xs">
              <b>{r.txCount}</b> tx
              {r.lastTxAt && <span className="text-muted-foreground"> · {dataSps(r.lastTxAt)}</span>}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: t('emp.status'),
        enableSorting: false,
        meta: { exportValue: (r) => (r.active ? 'aktywny' : 'zablokowany') },
        cell: ({ row }) =>
          row.original.active ? (
            <Badge variant="success">{t('emp.statusActive')}</Badge>
          ) : (
            /**
             * ⚠️ CZERWONY, NIE SZARY. Szara plakietka czytała się jak „brak danych", a to jest
             * STAN, który tłumaczy, dlaczego ktoś nie wyda towaru — zwłaszcza odkąd domyślny widok
             * pokazuje wszystkich i te wiersze leżą wymieszane z aktywnymi.
             */
            <Badge variant="destructive" title={t('emp.statusBlockedHint')}>
              {t('emp.statusBlocked')}
            </Badge>
          ),
      },
      /**
       * ⚠️ KOLUMNA AKCJI JEST TERAZ ZAWSZE, nie tylko dla admina. Wcześniej istniała wyłącznie
       * z myślą o edycji, a doszła pozycja, którą wolno OGLĄDAĆ każdemu z uprawnieniem do kadry:
       * dozwolone operacje systemowe. Ukrycie całej kolumny odebrałoby ten wgląd, mimo że API go
       * daje — a to menu kłamiące o uprawnieniach, czyli dokładnie ten błąd, który już raz
       * naprawialiśmy w nawigacji.
       */
      {
        id: 'actions',
        header: t('table.actions'),
        enableSorting: false,
        meta: { align: 'right' as const },
        cell: ({ row }) =>
          /**
           * ⚠️ OSOBA USUNIĘTA Z AUTOMATÓW NIE MA W SPS WIERSZA — edycja i operacje poszłyby w próżnię.
           * Zostaje jedna czynność: trwałe usunięcie z Vendi (powrót karty = dodanie osoby z tym numerem).
           */
          row.original.usunietaZAutomatow ? (
            <div className="flex justify-end gap-1">
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('emp.usunTrwale')}
                  aria-label={`${t('emp.usunTrwale')} ${row.original.name}`}
                  onClick={() => setUsuwanie({ numery: [row.original.spsUserNumber], trwale: true })}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                title={t('ops.title')}
                aria-label={`${t('ops.title')} ${row.original.name}`}
                onClick={() => setOperacjeFor(row.original)}
              >
                <KeySquare />
              </Button>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('common.edit')}
                  aria-label={`${t('common.edit')} ${row.original.name}`}
                  onClick={() => {
                    setEditing(row.original);
                    setModalOpen(true);
                  }}
                >
                  <Pencil />
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('emp.usunZAutomatow')}
                  aria-label={`${t('emp.usunZAutomatow')} ${row.original.name}`}
                  onClick={() => setUsuwanie({ numery: [row.original.spsUserNumber] })}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          ),
      } satisfies ColumnDef<EmployeeRow>,
    ],
    [t, isAdmin],
  );

  const fetchAllRows = useCallback(async (): Promise<EmployeeRow[]> => {
    if (!groupId) return [];
    const out: EmployeeRow[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await listEmployees({ ...params, page, pageSize: 500 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params, groupId]);

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  const pd = data?.summary;

  /**
   * ⚠️ WYSPA PROWADZONA PRZEZ HR+ (06.10.2026): ludzi dodaje tylko HR+ — serwer i tak odmówi (409),
   * ale przycisk, który zawsze kończy się odmową, uczy ludzi ignorować komunikaty. Stąd baner
   * i wyłączone „Dodaj"/„Importuj" — z zamówioną informacją, że ręczne zmiany zostaną nadpisane.
   */
  const hrPlus = island?.hrPlus === true;

  return (
    <div className="space-y-4">
      {hrPlus && (
        <div className="rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 px-3 py-2 text-sm">
          {t('emp.hrPlusBaner')}
        </div>
      )}
      {/* KAFELKI — zawsze o CAŁEJ wyspie, niezależnie od filtrów listy. Liczenie ich
          z przefiltrowanego zbioru sprawiłoby, że ten sam kafelek pokazuje raz stan wyspy,
          a raz stan tego, co akurat widać — i nie da się tego odróżnić, patrząc na ekran. */}
      {pd && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: t('emp.kpi.all'), value: pd.wszyscy, tone: '' },
            { label: t('emp.kpi.active'), value: pd.aktywni, tone: 'text-[var(--success)]' },
            { label: t('emp.kpi.blocked'), value: pd.zablokowani, tone: pd.zablokowani > 0 ? 'text-[var(--warning)]' : '' },
            { label: t('emp.kpi.inGroups'), value: pd.wGrupach, tone: '' },
            { label: t('emp.kpi.withLimits'), value: pd.zLimitami, tone: '' },
            {
              label: t('emp.kpi.activeRecently').replace('{d}', String(data!.oknoDni)),
              value: pd.aktywniOstatnio,
              tone: '',
            },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border border-border bg-card p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</p>
              <p className={`mt-0.5 text-2xl font-semibold tabular-nums ${k.tone}`}>{nf.format(k.value)}</p>
            </div>
          ))}
        </div>
      )}

      {/*
        ⚠️ BEZ DWÓCH RAMEK POD LISTĄ — zgłoszone wprost (28.08.2026): odsetek osób pobierających
        na kilku automatach oraz rozjazd dwóch postaci centrum kosztowego.

        ⚠️ SAME LICZBY NIE ZNIKAJĄ Z SYSTEMU, tylko przestają stać na tym ekranie: odsetek
        „wielomaszynowych" jest dziś argumentem, który już wygrał (limity liczą się z całej wyspy,
        `wydaniaRodzenstwa`), a rozjazd centrum kosztowego widać przy KONKRETNEJ osobie na jej
        karcie — tam, gdzie się go naprawia.
      */}
      <div className="grid gap-4 xl:grid-cols-[1fr_20rem]">
        <div className="min-w-0">
      <DataTable<EmployeeRow>
        title={t('nav.employees')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName={`pracownicy-${island?.code ?? 'wyspa'}`}
        fetchAllRows={fetchAllRows}
        reportKey="employees"
        reportGroupId={groupId}
        actions={
          isAdmin ? (
            <>
              {/*
                ⚠️ IMPORT OBOK DODAWANIA, NIE W ŚRODKU OKNA DODAWANIA. To dwie różne czynności:
                jedna zakłada osobę, o której coś się wie, druga wpuszcza listę od klienta.
                Wciśnięcie importu w formularz jednego pracownika kazałoby wybierać drogę
                człowiekowi, który już zaczął wypełniać pola.
              */}
              <Button
                variant="outline"
                disabled={hrPlus}
                title={hrPlus ? t('emp.hrPlusZablokowane') : undefined}
                onClick={() => setImportOtwarty(true)}
              >
                <Upload />
                {t('emp.importuj')}
              </Button>
              <Button variant="outline" onClick={() => setUsuwanie({})}>
                <Trash2 />
                {t('emp.usunKarty')}
              </Button>
              <Button
                disabled={hrPlus}
                title={hrPlus ? t('emp.hrPlusZablokowane') : undefined}
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                <Plus />
                {t('emp.add')}
              </Button>
            </>
          ) : null
        }
        aktywneFiltry={aktywneFiltry}
        onWyczyscFiltry={wyczyscFiltry}
        filters={
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('emp.group')}
              <Select
                value={employeeGroupId}
                onChange={(e) => {
                  setEmployeeGroupId(e.target.value);
                  resetPage();
                }}
                className="h-8 w-56 text-sm"
              >
                <option value="">{t('emp.allGroups')}</option>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g._count.members})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('emp.machine')}
              <Select
                value={machineId}
                onChange={(e) => {
                  setMachineId(e.target.value);
                  resetPage();
                }}
                className="h-8 w-56 text-sm"
              >
                <option value="">{t('tx.allMachines')}</option>
                {(machines.data?.rows ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    VMID {m.vmid} — {m.name}
                  </option>
                ))}
              </Select>
              {machines.error && (
                <span className="text-xs text-destructive">{t('tx.machineFilterUnavailable')}</span>
              )}
            </label>

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-3.5 accent-[var(--primary)]"
                checked={onlyMultiMachine}
                onChange={(e) => {
                  setOnlyMultiMachine(e.target.checked);
                  resetPage();
                }}
              />
              {t('emp.onlyMulti')}
            </label>

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-3.5 accent-[var(--primary)]"
                checked={usunieci}
                onChange={(e) => {
                  setUsunieci(e.target.checked);
                  resetPage();
                }}
              />
              {t('emp.filtrUsunieci')}
              {(data?.summary.usunieci ?? 0) > 0 && <span className="text-muted-foreground">({data?.summary.usunieci})</span>}
            </label>

            {/* ⚠️ TRÓJSTAN, NIE PTASZEK. Ptaszek umiał „ukryj nieaktywnych" i „pokaż wszystkich",
                ale nie „pokaż WYŁĄCZNIE nieaktywnych" — a to jedyne pytanie, które się przy tym
                filtrze realnie zadaje: kto został w SPS po odejściu z firmy. Na DBW takich osób
                są 3 na 523, więc bez tego trzeba je było wyłapywać wzrokiem. */}
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('emp.state')}
              <Select
                value={stan}
                /** Filtr usuniętych pokazuje wszystkich usuniętych — stan konta nie ma wtedy znaczenia. */
                disabled={usunieci}
                onChange={(e) => {
                  setStan(e.target.value as 'aktywni' | 'nieaktywni' | 'wszyscy');
                  resetPage();
                }}
                className="h-8 w-40 text-sm"
              >
                <option value="aktywni">{t('emp.stateActive')}</option>
                <option value="nieaktywni">{t('emp.stateInactive')}</option>
                <option value="wszyscy">{t('emp.stateAll')}</option>
              </Select>
            </label>

            {(data?.hrPlusUstawiony === true || hrFiltr !== '') && (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('emp.hr.filtr')}
                <Select
                  value={hrFiltr}
                  onChange={(e) => {
                    setHrFiltr(e.target.value as '' | 'z' | 'spoza' | 'wyjatki');
                    resetPage();
                  }}
                  className="h-8 w-40 text-sm"
                >
                  <option value="">{t('emp.stateAll')}</option>
                  <option value="z">{t('emp.hr.filtrZ')}</option>
                  <option value="spoza">{t('emp.hr.filtrSpoza')}</option>
                  <option value="wyjatki">{t('emp.hr.filtrWyjatki')}</option>
                </Select>
              </label>
            )}

            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('emp.costCenter')}
              <Select
                value={costCenter}
                onChange={(e) => {
                  setCostCenter(e.target.value);
                  resetPage();
                }}
                className="h-8 w-48 text-sm"
              >
                <option value="">{t('emp.allCostCenters')}</option>
                {/* ⚠️ Kody z DANYCH PRACOWNIKÓW, nie ze słownika centrów. Na DBW encji centrów
                    nie ma ani jednej, a `Users.COSTCENTER` jest wypełniony u wszystkich —
                    lista ze słownika byłaby tam pusta i filtr wyglądałby na zepsuty. */}
                {(centraFiltr.data?.podpowiedzi ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.wUzyciu > 0 ? `${c.code} (${c.wUzyciu})` : c.code}
                  </option>
                ))}
              </Select>
            </label>

            {/*
              ⚠️ PRZYCISK „WYCZYŚĆ" PRZENIÓSŁ SIĘ NA PASEK NAD TABELĄ (02.09.2026). Tutaj był
              widoczny wyłącznie po ROZWINIĘCIU panelu filtrów — czyli nie tam, gdzie widać, że
              lista jest przycięta. Dwa przyciski robiące to samo byłyby gorsze niż jeden.
            */}
          </div>
        }
      />
        </div>

        {/* PRACOWNICY WG GRUPY — rozkład, nie ranking. Grupy puste są pominięte, bo słupek
            o długości zero nie niesie informacji, a lista rośnie z każdą nieużywaną grupą. */}
        <aside className="rounded-lg border border-border bg-card p-4">
          <h3 className="text-sm font-medium">{t('emp.byGroup')}</h3>
          {(data?.wgGrupy.length ?? 0) === 0 ? (
            <p className="mt-3 text-xs text-muted-foreground">{t('emp.byGroupEmpty')}</p>
          ) : (
            <div className="mt-3 space-y-1.5">
              {data!.wgGrupy.map((g) => {
                // skala do NAJWIĘKSZEJ grupy, nie do sumy: przy dwudziestu grupach słupki
                // liczone od sumy byłyby nierozróżnialnymi kreskami
                const max = data!.wgGrupy[0]!.count || 1;
                return (
                  <button
                    key={g.id}
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-xs transition-colors hover:bg-accent"
                    title={t('emp.byGroupFilter')}
                    onClick={() => {
                      setEmployeeGroupId(employeeGroupId === g.id ? '' : g.id);
                      resetPage();
                    }}
                  >
                    <span className={`w-24 shrink-0 truncate ${employeeGroupId === g.id ? 'font-semibold' : ''}`}>
                      {g.name}
                    </span>
                    <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                      <span
                        className="block h-full rounded-full bg-[var(--primary)]"
                        style={{ width: `${Math.round((g.count / max) * 100)}%` }}
                      />
                    </span>
                    <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">{g.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </aside>
      </div>

      {modalOpen && (
        <EmployeeModal
          key={editing?.id ?? 'new'}
          open={modalOpen}
          onOpenChange={setModalOpen}
          editing={editing}
          groupId={groupId}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
          /**
           * ⚠️ ZAMYKAMY OKNO EDYCJI I OTWIERAMY OSOBNE, zamiast zagnieżdżać jedno w drugim.
           * Zmiana numeru to inna operacja niż zapis pracownika (kaskada po czterech tabelach),
           * więc nie powinna dzielić przycisku „Zapisz" z resztą formularza.
           */
          onZmienNumer={
            editing
              ? () => {
                  setNumerFor(editing);
                  setModalOpen(false);
                }
              : undefined
          }
        />
      )}

      {usuwanie !== null && groupId && (
        <UsuwanieKart
          groupId={groupId}
          numery={usuwanie.numery}
          trwale={usuwanie.trwale}
          onClose={() => setUsuwanie(null)}
          onUsunieto={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}
      {importOtwarty && (
        <ImportPracownikow
          groupId={groupId}
          /**
           * ⚠️ TA SAMA LISTA AUTOMATÓW CO W FORMULARZU JEDNEGO PRACOWNIKA (`machines.picker`),
           * bo import ma dawać ten sam efekt co dodanie ludzi po kolei. Druga, osobno pobierana
           * lista mogłaby się od tamtej różnić i nikt by nie wiedział dlaczego.
           */
          automaty={(machines.data?.rows ?? []).map((m) => ({ id: m.id, nazwa: `VMID ${m.vmid} — ${m.name}` }))}
          onClose={() => setImportOtwarty(false)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}

      {numerFor && (
        <ZmianaNumeruKarty
          employeeId={numerFor.id}
          islandId={groupId}
          obecnyNumer={numerFor.spsUserNumber}
          nazwisko={numerFor.name}
          onClose={() => setNumerFor(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}

      {operacjeFor && (
        <OperacjeModal
          cel="employee"
          id={operacjeFor.id}
          islandId={groupId}
          canEdit={isAdmin}
          onClose={() => setOperacjeFor(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}
    </div>
  );
}
